<template>
    <div class="apprentice-simple-card">
        <div class="row explanation">
            <div class="col-12">
                <h2 class="title">{{ $t('quest.explanation') }}</h2>

                <p class="info-text">
                    {{
                        $t(
                            'quest.in_this_part_you_can_teach_the_student_to_plan_you_can_monitor_the_students_planning_by_using_timers_for_each_assignment',
                        )
                    }}
                </p>

                <h5 class="card-title">{{ $t('quest.for_example') }}</h5>
                <p>
                    {{
                        $t(
                            'quest.you_want_the_students_to_do_a_total_of_7_assignments_in_7_months_and_they_have_per_assignment_in_that_case_you_create_7_timers',
                        )
                    }}
                </p>

                <p>
                    {{
                        $t(
                            'quest.quests_starts_the_timer_of_an_assignment_at_the_time_you_specify_for_example_1_september_the_timer_starts_running_and_is_initially_set_to_100_percent_and_after_a_month_gradually_decreases_this_means_that_if_the_student_is_on_time_he_will_receive_an_assessment_that_counts_for_100_percent_but_if_he_is_later_than_the_set_time_one_month_than_the_timer_will_no_longer_be_at_100_percent_but_at_for_example_90_percent',
                        )
                    }}
                </p>

                <p>
                    {{
                        $t(
                            'quest.here_there_is_the_option_to_start_the_timer_at_for_example_110_percent_so_that_students_earns_a_small_bonus_if_he_submits_the_assignment_earlier_than_planned_the_percentages_are_calculated_based_on_the_assessment_you_give_to_the_assignment',
                        )
                    }}
                </p>
                <p>&nbsp;</p>
            </div>

            <div class="col-12 col-lg-4 start-bonus">
                <h5 class="title">{{ $t('quest.starting_percentage_bonus') }}</h5>
                <p>
                    {{ $t('quest.quest.bonus_when_ahead_of_planning.') }}
                </p>

                <div class="bonus-value">
                    <b-form-input
                        v-model="deadlineSettings.percentage"
                        placeholder="Percentage"></b-form-input>
                </div>
            </div>

            <div class="col-12 col-lg-4 deliver-date">
                <h5 class="title">{{ $t('quest.days_until_hand_in_moment') }}</h5>
                <p>
                    {{
                        $t(
                            'quest.here_you_can_enter_after_how_many_days_the_timer_starts_to_count_down_and_the_bonus_malus_gradually_decreases_for_example_you_can_set_here_that_the_student_will_see_his_bonus_percentage_decrease_from_20_days_prior_to_the_submission_date_in_that_case_enter_20_here_if_you_do_not_want_to_work_with_a_bonus_and_only_with_a_penalty_enter_0_here_the_timer_will_then_gradually_decrease_from_100_percent_at_th_return_time_you_entered',
                        )
                    }}
                </p>

                <div class="deliver-date-due-date">
                    <b-form-input
                        v-model="deadlineSettings.start_days"
                        :placeholder="$t('quest.amount_of_days')"></b-form-input>
                </div>
            </div>

            <div class="col-12 col-lg-4 bonus-percentage">
                <h5 class="title">{{ $t('quest.percentage_by_day') }}</h5>
                <p>
                    {{
                        $t(
                            'quest.each_day_the_timer_will_decrease_slightly_and_indicate_a_different_percentage_you_can_determine_by_how_much_percent_the_timer_decreases_per_day_if_you_want_the_timer_to_decrease_by_0_5_percent_per_day_you_can_enter_0_5_here',
                        )
                    }}
                </p>

                <div class="bonus-percentage-content">
                    <b-form-input
                        v-model="deadlineSettings.factor"
                        :placeholder="$t('quest.percentage_by_day')"></b-form-input>
                </div>
            </div>

            <div class="col-12 save">
                <button
                    class="btn btn-primary"
                    type="button"
                    @click="saveSettings">
                    {{ $t('common.save') }}
                </button>
            </div>
        </div>

        <div class="row">
            <div class="col-12">
                <div class="delivery-moments">
                    <div class="new-delivery-moment">
                        <button
                            class="btn btn-primary btn-sm float-right"
                            type="button"
                            @click="newDeadline">
                            {{ $t('quest.new_hand_in_moment') }}
                        </button>
                    </div>

                    <ul
                        class="list-unstyled delivery-moments-group"
                        v-if="deadlines">
                        <b-media
                            class="delivery-moment"
                            v-for="(deadline, index) in deadlines"
                            vertical-align="center"
                            tag="li"
                            :key="deadline.id">
                            <template #aside>
                                <i class="fad fa-alarm-exclamation icon"></i>
                            </template>
                            <h6 class="title">
                                Timer {{ index + 1 }}
                                <div class="deadline-actions">
                                    <b-button
                                        class="btn-settings"
                                        variant="primary-icon-only"
                                        @click="editDeadline(deadline.id)"
                                        :v-b-tooltip="$t('common.edit')"></b-button>

                                    <b-button
                                        class="btn-remove"
                                        variant="danger-icon-only"
                                        @click="deleteDeadline(deadline.id)"
                                        :v-b-tooltip="$t('common.delete')"></b-button>
                                </div>
                            </h6>
                            <div class="date">
                                {{ deadline.shortdate }}
                            </div>
                        </b-media>
                    </ul>
                </div>
            </div>
        </div>

        <b-modal
            id="quest-edit-deadline-modal"
            title="Deadline"
            :ok-title="$t('common.save')"
            :cancel-title="$t('common.cancel')"
            cancel-variant="outline-primary"
            modal-class="apprentice-default-modal"
            @ok="saveDeadline()">
            <div class="deadline">
                <b-form-datepicker
                    class="apprentice-date-picker btn-date-group"
                    v-model="form_date"
                    start-weekday="1"
                    now-button
                    reset-button
                    right
                    today-variant="primary"
                    nav-button-variant="primary"
                    locale="nl"
                    :aria-controls="$t('quest.choose_date')"
                    label-reset-button="reset"
                    :label-no-date-selected="$t('quest.no_date_selected')"
                    menu-class="datedropdown"
                    :date-format-options="{
                        year: 'numeric',
                        month: 'short',
                        day: '2-digit',
                        weekday: 'short',
                    }"></b-form-datepicker>
            </div>
        </b-modal>
    </div>
</template>

<script>
export default {
    name: 'design-2021-quest-edit-deadlines-component',
    props: ['quest'],
    data() {
        return {
            deadlines: this.quest.deadlines,
            deadlineSettings: this.quest.deadline_settings,
            form_id: null,
            form_date: null,
        };
    },
    methods: {
        saveSettings() {
            this.alert.saving();

            var data = {
                percentage: this.deadlineSettings.percentage,
                start_days: this.deadlineSettings.start_days,
                factor: this.deadlineSettings.factor,
            };

            this.axios.post(this.routes.questEditDeadlineSettings(this.quest.id), data).then(
                (response) => {
                    this.deadlineSettings = response.data.deadlineSettings;
                    this.alert.success();
                },
                (error) => {
                    this.alert.ajaxError(error);
                },
            );
        },
        newDeadline() {
            this.form_id = null;
            this.form_date = null;

            this.$bvModal.show('quest-edit-deadline-modal');
        },
        saveDeadline() {
            if (this.form_id == null) {
                this.addDeadline();
            } else {
                this.saveEditDeadline();
            }
        },
        addDeadline() {
            this.alert.saving();

            var data = {
                date: this.form_date,
            };

            this.axios.post(this.routes.questEditDeadlineAdd(this.quest.id), data).then(
                (response) => {
                    this.deadlines = response.data.deadlines;
                    this.alert.success();
                },
                (error) => {
                    this.alert.ajaxError(error);
                },
            );
        },
        editDeadline(id) {
            for (const deadline of this.deadlines) {
                if (deadline.id == id) {
                    this.form_id = id;
                    this.form_date = deadline.date;

                    this.$bvModal.show('quest-edit-deadline-modal');
                }
            }
        },
        saveEditDeadline() {
            this.alert.saving();

            var data = {
                id: this.form_id,
                date: this.form_date,
            };

            this.axios.post(this.routes.questEditDeadlineEdit(this.quest.id), data).then(
                (response) => {
                    this.deadlines = response.data.deadlines;
                    this.alert.success();
                },
                (error) => {
                    this.alert.ajaxError(error);
                },
            );
        },
        deleteDeadline(id) {
            this.alert
                .confirm(
                    this.$t('quest.delete_deadline'),
                    this.$t('quest.are_you_sure_you_want_to_delete_this_deadline'),
                )
                .then((confirm) => {
                    if (confirm) {
                        this.alert.saving();

                        this.axios.delete(this.routes.questEditDeadlineDelete(this.quest.id, id)).then(
                            (response) => {
                                this.deadlines = this.deadlines.filter((deadline) => deadline.id !== id);
                                this.alert.success();
                            },
                            (error) => {
                                this.alert.ajaxError(error);
                            },
                        );
                    }
                });
        },
    },
};
</script>

<style scoped lang="scss">
.card-body {
    text-align: left;
}

.explanation {
    border-bottom: 1px solid $border-color;
    padding-bottom: 21px;
    margin-bottom: 30px;
}

.explanation-card,
.start-percentage-bonus-card,
.deliver-date-card,
.bonus-percentage-content-card {
    border: none;
    border-bottom: 30px;
}

.explanation-card .card-body,
.start-percentage-bonus-card .card-body,
.deliver-date-card .card-body,
.bonus-percentage-content-card .card-body {
    padding: 10px 15px;
    color: $text-color;
}

.card-title {
    margin-bottom: 15px;
    line-height: 1.1;
}

.explanation-card .title {
    font-size: 1.75rem;
}

.explanation-card .card-title {
    margin-top: 21px;
    margin-bottom: 5px;
}

.explanation-card .card-body {
    font-size: 0.875rem;
}

.save {
    text-align: right;
}

.delivery-moments {
    display: flex;
    justify-content: flex-start;
    flex-direction: column;
}

.new-delivery-moment {
    justify-content: flex-end;
}

.delivery-moments-group {
    margin-top: map-get($grid-gutter-spacing, 'forty-two');
    display: flex;
    flex-wrap: wrap;
    flex-direction: column;
    justify-content: flex-start;

    .delivery-moment {
        display: flex;
        justify-content: space-between;
        align-items: center;
        border-bottom: 3px dotted $border-color;
        margin-bottom: 8px;

        .media-aside {
            color: $danger;

            .icon {
                font-size: 1.5625rem;
            }
        }

        .media-body {
            padding: map-get($grid-gutter-spacing, 'eight') 0;
        }

        .title {
            display: flex;
            align-items: center;
            flex-direction: row;
            justify-content: space-between;
            margin-bottom: 0;

            .deadline-actions {
                display: flex;
                flex: 0 0 auto;
                margin-left: auto;
                align-self: baseline;

                .btn:not(:last-child) {
                    margin-right: map-get($grid-gutter-spacing, 'eight');
                }
            }
        }

        .date {
            color: $text-muted-color;
            font-size: 0.75rem;
            font-weight: $font-weight-medium;
        }
    }
}

@media (min-width: map-get($grid-breakpoints, 'sm')) {
    .delivery-moments-group {
        align-items: center;
        flex-direction: row;
        justify-content: space-between;

        .delivery-moment {
            width: calc(50% - 15px);
        }
    }
}
</style>
